import type { DeviceResponse, MyDevicesResponse } from "@jti/rest-api-types";

import { setUsersDeviceStatus } from "../../../utils";
import type { RestResponseType } from "../../Rest";
import { Rest } from "../../Rest";
import type { DeviceApiContract } from "../DeviceApi.types";

/** @deprecated Legacy proxy implementation — superseded by EVO. To be deleted after production verification. */
export class DeviceApiLegacy extends Rest implements DeviceApiContract {
  readonly name = "DeviceApi";
  constructor(baseUrl: string, tenantToken: string, ciam_token: string, jwtToken: string) {
    super(`${baseUrl}/device-inventory/v1`);

    super.setCommonHeader("Authorization", `Bearer ${jwtToken}`);
    super.setCommonHeader("DIS_DES_AK_TENANT_TOKEN", tenantToken);
    super.setCommonHeader("X-DES-CONSUMER-IAM-TOKEN", ciam_token);
  }

  async myDevices(userId?: string): Promise<RestResponseType<MyDevicesResponse>> {
    if (!userId) {
      return Promise.reject(new Error("User id is not defined"));
    }

    const response = await this.request<MyDevicesResponse>(`/devices?userId=${userId}`, {
      method: "GET",
    });

    const hasUserDevices = Array.isArray(response?.data) && response?.data?.length > 0;
    setUsersDeviceStatus(hasUserDevices);

    return response;
  }

  async registerDevice(registrationCode: string, userId?: string, token?: string, ciam_token?: string) {
    if (userId === undefined) {
      return Promise.reject(new Error("Consumer ID is not defined"));
    }

    return this.request<DeviceResponse>(
      "/devices",
      {
        headers: {
          ...(!!token && { Authorization: `Bearer ${token}` }),
          ...(!!ciam_token && { "X-DES-CONSUMER-IAM-TOKEN": ciam_token }),
        },
        method: "POST",
        body: JSON.stringify({ deviceId: registrationCode, consumerId: userId }),
      },
      true
    );
  }

  async deleteDevice(id: string, userId?: string, ciamToken?: string): Promise<RestResponseType<MyDevicesResponse>> {
    if (userId === undefined) {
      return Promise.reject(new Error("Consumer ID is not defined"));
    }

    const overrideHeaders = ciamToken !== undefined ? { headers: { "X-DES-CONSUMER-IAM-TOKEN": ciamToken } } : {};

    return this.request(
      `/devices/${encodeURIComponent(id)}/unassign`,
      {
        method: "PUT",
        body: JSON.stringify({ consumerId: userId }),
        ...overrideHeaders,
      },
      true
    );
  }

  async checkDeviceIdForRegistration(id: string): Promise<undefined> {
    return this.request<undefined>(`/devices/validation/${id}`, { method: "GET" }, true);
  }

  async assignDeviceToUser(deviceId: string, userId?: string, ciamToken?: string): Promise<DeviceResponse> {
    if (userId === undefined) {
      return Promise.reject(new Error("Consumer ID is not defined"));
    }

    const overrideHeaders = ciamToken !== undefined ? { headers: { "X-DES-CONSUMER-IAM-TOKEN": ciamToken } } : {};

    return this.request(
      `/devices/${deviceId}/consumer`,
      { method: "PATCH", body: JSON.stringify({ consumerId: userId }), ...overrideHeaders },
      true
    );
  }
}
